import { useId, useRef, useState } from 'react';

import { Input } from '@/components/ui/input';
import { Label, RequiredMark } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

import { useFieldError } from '../builder/useFieldError';

/**
 * A text field that reports its value on blur or Enter, never per keystroke.
 *
 * This is the whole of research D2's field rule, in one component. Committing
 * per keystroke would make each character its own undo entry, so undo would
 * mean "delete the last letter" and walking back a sentence would take fifty
 * presses. It would also mark the document dirty on a keystroke the user then
 * deletes.
 *
 * The value is held locally while editing and re-synced when the committed
 * value changes underneath — which happens on undo, on a save's response, and
 * when the same field is edited from elsewhere.
 */
export function CommitField({
  label,
  value,
  onCommit,
  multiline = false,
  rows = 4,
  readOnly = false,
  hint,
  placeholder,
  mono = false,
  field,
  required = false,
  maxLength,
}: {
  label: string;
  value: string;
  onCommit: (next: string) => void;
  multiline?: boolean;
  rows?: number;
  readOnly?: boolean;
  hint?: string;
  placeholder?: string;
  mono?: boolean;
  /**
   * The name this field has in the workflow document — `prompt`, `when`, and so
   * on, exactly as `ImportViolation.field` reports it.
   *
   * Given one, the field finds its own refusal in context and shows it in place
   * of the hint: the hint says what the field is for, which is advice the author
   * no longer needs once they are being told what is wrong with what they wrote.
   * Two paragraphs would also make `aria-describedby` read both, burying the
   * actionable one.
   *
   * Omitted by fields the service never names, which simply never mark.
   */
  field?: string;
  /** Renders the required mark and sets `aria-required`. */
  required?: boolean;
  /**
   * The most characters the field takes; a paste is cut at it. Given one, a
   * `n / max` count shows beside the hint, so the limit is seen before it is hit.
   */
  maxLength?: number;
}) {
  const id = useId();
  const hintId = useId();
  const errorId = useId();
  const { error } = useFieldError(field);
  const [draftValue, setDraftValue] = useState(value);
  const [lastCommitted, setLastCommitted] = useState(value);

  // Re-sync when the committed value moves for a reason other than this field:
  // undo, a save response, or an edit made elsewhere. Without this, undo would
  // visibly restore the document while the field kept showing the old text.
  //
  // Adjusted during render rather than in an effect — React's documented
  // pattern for a prop-derived reset. An effect would paint the stale value
  // first and then correct it, which is a visible flicker on every undo.
  if (value !== lastCommitted) {
    setLastCommitted(value);
    setDraftValue(value);
  }

  /**
   * Set by Escape so the blur it causes does not commit.
   *
   * `setDraftValue` is asynchronous, so the `blur()` that follows still sees
   * the typed text in its closure and would commit the very edit Escape just
   * abandoned. A ref is read synchronously, which is the whole point of using
   * one here.
   */
  const cancelledRef = useRef(false);

  const commit = () => {
    if (readOnly) return;
    if (cancelledRef.current) {
      cancelledRef.current = false;
      return;
    }
    if (draftValue !== value) onCommit(draftValue);
  };

  const abandon = (element: HTMLElement) => {
    cancelledRef.current = true;
    setDraftValue(value);
    element.blur();
  };

  const shared = {
    id,
    value: draftValue,
    readOnly,
    placeholder,
    maxLength,
    'aria-invalid': error ? true : undefined,
    'aria-required': required || undefined,
    // The error wins the description slot while it exists — see `error` above.
    'aria-describedby': error ? errorId : hint ? hintId : undefined,
    onChange: (event: { target: { value: string } }) => {
      setDraftValue(event.target.value);
    },
    onBlur: commit,
    className: cn(
      mono && 'font-brand-mono',
      readOnly && 'text-muted-foreground',
    ),
  };

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-0.5">
        <Label htmlFor={id}>{label}</Label>
        {required && <RequiredMark />}
      </div>
      {multiline ? (
        <Textarea
          {...shared}
          rows={rows}
          // Enter inserts a newline in a textarea, so it cannot also commit.
          // Blur is the only commit here, which is why the hint says so.
          onKeyDown={(event) => {
            if (event.key === 'Escape') abandon(event.currentTarget);
          }}
        />
      ) : (
        <Input
          {...shared}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              commit();
            }
            if (event.key === 'Escape') {
              // Abandon the edit rather than commit it — the same escape hatch
              // every dialog offers, and the reason a mistyped id is not a
              // problem.
              abandon(event.currentTarget);
            }
          }}
        />
      )}
      {error ? (
        // `role="alert"` because this arrives from the server after a submit,
        // not as the user types — the same reason the identifier error below
        // carries one.
        <p id={errorId} role="alert" className="text-destructive text-xs">
          {error}
        </p>
      ) : (
        hint && (
          <p id={hintId} className="text-muted-foreground text-xs">
            {hint}
          </p>
        )
      )}
      {maxLength !== undefined && (
        <p className="text-muted-foreground self-end text-xs tabular-nums">
          {`${String(draftValue.length)} / ${String(maxLength)}`}
        </p>
      )}
    </div>
  );
}
